/**
 * **Ubah PIN saya** — di menu ☰ layar kasir (Imron, 2026-08-07).
 *
 * ### ⛔⛔ Kenapa ini bukan kenyamanan
 * Sampai sekarang satu-satunya jalur menyetel PIN adalah layar Staf di portal, yang menuntut
 * `master:write` — artinya **penyelialah yang mengetik PIN tiap kasir**. Konsekuensinya bukan soal
 * kepraktisan: selama PIN seseorang pernah diketik orang lain dan tak pernah bisa diganti,
 * pertanyaan *"siapa yang membuka laci itu"* selalu punya jawaban kedua yang sah — *"bisa jadi
 * manajernya"*. Dan PIN-lah yang mengizinkan buka laci, void, serta persetujuan promo (K23).
 *
 * ### ⛔ PIN LAMA wajib
 * Sesi POS hidup sepanjang shift. Tanpa syarat itu, tablet yang ditinggal terbuka satu menit cukup
 * untuk memindahkan identitas seseorang. Server yang menegakkan (`AUTH-401-PIN`); kolomnya di sini
 * hanya membuat penegakan itu terlihat sebelum ditolak.
 *
 * ⚠️ Tak disaring izin apa pun: mengganti PIN sendiri adalah hak SETIAP staf, termasuk yang paling
 * sedikit izinnya. Menggantungkannya pada izin berarti kasir yang PIN-nya terlihat orang harus
 * menunggu penyelia — dan sementara itu PIN-nya tetap terlihat.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { staffSession } from '../api/session';

export function ChangePinButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lama, setLama] = useState('');
  const [baru, setBaru] = useState('');
  const [ulang, setUlang] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const staf = staffSession();
  if (staf === undefined) return null;

  const cocok = baru !== '' && baru === ulang;

  const tutup = () => {
    setOpen(false); setLama(''); setBaru(''); setUlang(''); setError(null); setOk(false);
  };

  return (
    <>
      <button
        type="button"
        className="action-secondary"
        data-testid="ubah-pin"
        onClick={() => { setOpen(true); }}
      >
        {t('cashier.pin.button')}
      </button>

      {open && (
        <div className="dialog" role="dialog" aria-label={t('cashier.pin.title')} data-testid="pin-dialog">
          <h2>{t('cashier.pin.title')}</h2>
          <p className="park-hint">{t('cashier.pin.hint')}</p>

          {/* ⚠️ `inputMode="numeric"` — papan angka, bukan papan huruf. Di tablet dapur/kasir itu
              bedanya dua ketukan tiap kali. */}
          <input
            className="park-input"
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            maxLength={8}
            placeholder={t('cashier.pin.current')}
            data-testid="pin-lama"
            value={lama}
            onChange={(e) => { setLama(e.target.value); }}
          />
          <input
            className="park-input"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            placeholder={t('cashier.pin.next')}
            data-testid="pin-baru"
            value={baru}
            onChange={(e) => { setBaru(e.target.value); }}
          />
          {/*
            ⭐ Ketik ulang: PIN yang salah ketik satu digit mengunci orangnya keluar dari terminalnya
            sendiri di tengah shift, dan yang bisa menolongnya cuma penyelia.
          */}
          <input
            className="park-input"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            placeholder={t('cashier.pin.repeat')}
            data-testid="pin-ulang"
            value={ulang}
            onChange={(e) => { setUlang(e.target.value); }}
          />

          {error !== null && <p className="error" data-testid="pin-error">{error}</p>}
          {ok && <p data-testid="pin-ok">{t('cashier.pin.ok')}</p>}

          <div className="dialog-actions">
            <button type="button" data-testid="pin-batal" onClick={tutup}>
              {t('cashier.pin.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="pin-simpan"
              disabled={busy || lama === '' || !cocok}
              title={cocok ? undefined : t('cashier.pin.mismatch')}
              onClick={() => {
                setBusy(true);
                setError(null);
                void api.changeOwnPin(staf.staffCode, lama, baru)
                  .then(() => {
                    setOk(true);
                    /* ⚠️ Dialognya ditutup sendiri sesudah dibaca — kasir sedang melayani antrean. */
                    setTimeout(tutup, 1200);
                  })
                  .catch((e: unknown) => {
                    setError(e instanceof Error ? e.message : String(e));
                  })
                  .finally(() => { setBusy(false); });
              }}
            >
              {t('cashier.pin.save')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
